import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Orders } from 'src/components/orders';
import { ReprocessPendingReviewButton } from './ReprocessPendingReviewButton';
import { useRouter } from 'src/hooks/useRouter';
import { routesMap } from 'src/routes';
import { useGetOrders } from 'src/services/apiService';
import { ContractorSelect } from '../../../components/ContractorSelect';
import { checkIsPermitted, useGetUserMe } from 'src/entities/user';
import { UserRole } from 'src/shared/api';
import { removeEmptyProps } from 'src/helpers/removeEmtyProperties';
import { OrderWithMeta } from 'src/types/api';
import { DateControl } from 'src/shared/ui/entity-control';
import { format } from 'date-fns';
import { Search } from '@mui/icons-material';

interface Props {}

export const OrdersPage: React.FunctionComponent<Props> = ({}) => {
  const { query, ...router } = useRouter();

  const [queryState, setQueryState] = useState({ query: '' });

  useEffect(() => {
    setQueryState(state => ({ ...state, query: query.query }));
  }, [query]);

  const userMeRequest = useGetUserMe();

  const outlets = query.outlets?.join(',');

  const variables = removeEmptyProps({
    limit: parseInt(query.limit, 10) || 100,
    offset: parseInt(query.offset, 10) || 0,
    state: query.state || 'processed',
    sortField: query.sortField,
    sortDirection: query.sortDirection,
    isMobile: query.isMobile,
    from: query.from && format(new Date(query.from), 'yyyy-MM-dd 00:00:00'),
    to: query.to && format(new Date(query.to), 'yyyy-MM-dd 23:59:59'),
    byUnit: query.byUnit,
    query: query.query,
    outlets,
    users: {
      true: null,
      false: !outlets && !query.byUnit ? userMeRequest.data?.id : null,
    }[
      String(checkIsPermitted([UserRole.ROLE_ADMIN], userMeRequest.data?.roles))
    ],
  });

  const request = useGetOrders({
    variables,
  });

  const handleOnChange = (data: any) =>
    push(
      removeEmptyProps({
        ...variables,
        query: data.query,
        offset: data.page && data.limit && data.page * data.limit,
        limit: data.limit,
      }),
    );

  const push = (query: any) =>
    router.push(`${routesMap.orders.getRoute()}?${new URLSearchParams(query)}`);

  const orders = useMemo(() => {
    if (!query.isMissedDocs && !query.isErrors) return request?.data?.data;
    const items = request?.data?.data || [];
    let filtered: OrderWithMeta[] = [];

    if (query.isMissedDocs) {
      filtered = items?.filter(
        order =>
          !order?.zakazesCount ||
          order?.zakazesCount !== order?.realizationsCount,
      );
    }
    if (query.isErrors) {
      filtered = items?.filter(order => order?.errorsCount);
    }

    return Array.from(
      new Map(filtered.map(order => [order.data.id, order])).values(),
    ).sort((a, b) =>
      a?.data?.id && b?.data?.id && a?.data?.id > b?.data?.id ? -1 : 1,
    );
  }, [request.data, query.isMissedDocs, query.isErrors]);

  return (
    <div>
      <Box mb={2} display="flex" alignItems="center">
        <Box mr={2}>
          <TextField
            sx={{ minWidth: '320px' }}
            label="Номер order/заказа/реализации"
            value={queryState.query || ''}
            onChange={e =>
              setQueryState(state => ({ ...state, query: e.target.value }))
            }
            onKeyDown={e =>
              e.key === 'Enter' && handleOnChange({ query: queryState.query })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={!queryState.query}
                    onClick={() => handleOnChange({ query: queryState.query })}
                  >
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box mr={2}>
          <FormControl>
            <InputLabel id="state-label">Статус</InputLabel>
            <Select
              labelId="state-label"
              value={variables.state}
              label="Age"
              onChange={e => push({ ...variables, state: e.target.value })}
            >
              <MenuItem value="draft">draft</MenuItem>
              <MenuItem value="review">review</MenuItem>
              <MenuItem value="processed">processed</MenuItem>
              <MenuItem value="waiting_realization">
                waiting_realization
              </MenuItem>
              <MenuItem value="deleted">deleted</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box mr={2}>
          <DateControl
            type="date"
            format="yyyy-MM-dd"
            onChange={value =>
              router.push(
                `${routesMap.orders.getRoute()}?${new URLSearchParams({
                  ...query,
                  from: value,
                })}`,
              )
            }
            value={query.from}
            editable
            label="От"
          />
        </Box>
        <Box mr={2}>
          <DateControl
            type="date"
            format="yyyy-MM-dd"
            onChange={value =>
              router.push(
                `${routesMap.orders.getRoute()}?${new URLSearchParams({
                  ...query,
                  to: value,
                })}`,
              )
            }
            value={query.to}
            editable
            label="До"
          />
        </Box>
      </Box>
      <Box mb={2} display="flex" alignItems="center">
        <Box mr={2}>
          <FormControlLabel
            label="Несозданные документы"
            checked={query.isMissedDocs}
            onChange={() =>
              router.push(
                routesMap.orders.getRoute(),
                removeEmptyProps({
                  ...query,
                  isMissedDocs: query.isMissedDocs ? null : true,
                }),
              )
            }
            control={<Checkbox />}
          />
          <FormControlLabel
            label="Ошибки"
            checked={query.isErrors}
            onChange={() =>
              router.push(
                routesMap.orders.getRoute(),
                removeEmptyProps({
                  ...query,
                  isErrors: query.isErrors ? null : true,
                }),
              )
            }
            control={<Checkbox />}
          />
        </Box>
        <Box mr={2}>
          <FormControlLabel
            label="Заказы подразделения"
            checked={query.byUnit}
            onChange={() =>
              router.push(
                routesMap.orders.getRoute(),
                removeEmptyProps({
                  ...query,
                  byUnit: query.byUnit ? null : true,
                }),
              )
            }
            control={<Checkbox />}
          />
        </Box>
                <Box mr={2}>
          <FormControlLabel
            label="Приложение"
            checked={query.isMobile}
            onChange={() =>
              router.push(
                routesMap.orders.getRoute(),
                removeEmptyProps({
                  ...query,
                  isMobile: query.isMobile ? null : true,
                }),
              )
            }
            control={<Checkbox />}
          />
        </Box>
      </Box>
      <Box mt={2}>
        <ContractorSelect
          onSelect={contarctor =>
            push({
              outlets:
                contarctor?.outlets?.map(item => item.id)?.join(',') || '',
            })
          }
        />
      </Box>
      <Box mt={2} display="flex" alignItems="center">
        <Box>
          <FormControl>
            <InputLabel id="sort-label">Сортировка</InputLabel>
            <Select
              sx={{ minWidth: '150px' }}
              labelId="sort-label"
              value={variables.sortField}
              label="Сортировка"
              onChange={e => push({ ...variables, sortField: e.target.value })}
            >
              <MenuItem value="createdAt">Дата создания</MenuItem>
              <MenuItem value="updatedAt">Дата обновления</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box ml={2}>
          <FormControl>
            <InputLabel id="sort-label">Направление</InputLabel>
            <Select
              sx={{ minWidth: '150px' }}
              labelId="sort-label"
              value={variables.sortDirection}
              label="Направление"
              onChange={e =>
                push({ ...variables, sortDirection: e.target.value })
              }
            >
              <MenuItem value="ASC">ASC</MenuItem>
              <MenuItem value="DESC">DESC </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button onClick={() => router.push(routesMap.orders.getRoute(), {})}>
          Очистить
        </Button>
        <ReprocessPendingReviewButton />
      </Box>
      <Box mt={2}>
        <Orders
          data={orders || []}
          loading={request.isLoading}
          totalCount={request?.data?.totalCount}
          // onFetch={() => refetch(variables)}
          onChange={handleOnChange}
          limit={variables.limit}
          offset={variables.offset}
        />
      </Box>
    </div>
  );
};
